import { useContext, useEffect, useState } from "react";
import {
  ContactListType,
  CreateEditContactType,
  defaultContact,
  TeacherListType,
} from "utils/interfaces";
import { motion } from "framer-motion";
import {
  createRecordRequest,
  getCollectionRequest,
} from "services/apiRequests";
import { MainContext } from "components/utils/Contexts";
import PopUpHeader from "../common/PopUpHeader";
import {
  defaultBgVariants,
  defaultPanelVariants,
} from "../common/defaultValues";
import UserCardsList from "../common/UserCardsList";
import SuccessPopUpMessage from "../common/SuccessPopUpMessage";
import CreateEditUser from "../common/CreateEditUser";
import { logoutFunction } from "services/authRequests";

interface AddTeacherPanelProps {
  selectedData?: TeacherListType;
  selectedTeachers: ContactListType[];
  handleClosePopUp: () => void;
  setSelectedTeachers: React.Dispatch<React.SetStateAction<ContactListType[]>>;
}

const AddTeacherPanel: React.FC<AddTeacherPanelProps> = ({
  selectedTeachers,
  handleClosePopUp,
  setSelectedTeachers,
}) => {
  // States definition
  const [teacherData, setTeacherData] = useState<TeacherListType[] | null>(
    null
  );
  const [createNewTeacher, setCreateNewTeacher] = useState(false);
  const [isLoadingData, setIsLoadingData] = useState(true);
  const [showSuccessPopUp, setShowSuccessPopUp] = useState(false);
  const [lastSelectedContact, setLastSelectedContact] =
    useState<ContactListType>(defaultContact);

  // Get the context values
  const { contextSchoolByRole } = useContext(MainContext);

  /**
   * Load the teacher data
   */
  useEffect(() => {
    getTeacherData();
  }, []);

  /**
   * Timer for the success pop up message
   */
  useEffect(() => {
    if (showSuccessPopUp) {
      // Starting the timer
      const timer = setTimeout(() => {
        // Once finished, close the pop up
        setShowSuccessPopUp(false);
        // And forget the last selected id
        setLastSelectedContact(defaultContact);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [showSuccessPopUp]);

  /**
   * Definition of the function when pressed back button
   */
  const handlePressBack = () => {
    // If create new was pressed, then come back to the regular option
    if (createNewTeacher) {
      // come back
      setCreateNewTeacher(false);
    }
    // Otherwise, this pop up is being used by itself, so just close it
    else handleClosePopUp();
  };

  /**
   * Function used to get data
   * @returns
   */
  const getTeacherData = async () => {
    // First of all, start the animation
    setIsLoadingData(true);

    // Get the data
    const response = await getCollectionRequest(
      `/api/users/class-admins?schoolId=${contextSchoolByRole._id}`
    );

    // Check if the response is successful
    if (!response.successful) {
      // Check if it's not successful because of a token expiration
      if (response.logout) {
        // alert(response.message);
        return await logoutFunction();
      }

      return [];
    }

    // Get the data
    const data = response.content;

    // Set the data
    setTeacherData(data);

    // Stop the loading data
    setIsLoadingData(false);

    return data;
  };

  /**
   * Definition of the Add or remove teacher on the list
   * @param id of the selected id in the pop up menu
   * @param action "add" or "remove" "selectedData._id" from "id"
   */
  const selectTeacherAction = (
    contact: ContactListType,
    action: "add" | "remove"
  ) => {
    // Add to the current selected contact data ids
    if (action === "add") {
      // Add to the ids list (and ensure prevent duplicated)
      setSelectedTeachers([...new Set([...selectedTeachers, contact])]);

      // Define the last selected contact
      setLastSelectedContact(contact);

      // Display show pop up message
      setShowSuccessPopUp(true);
    }
    // Or delete from it
    else if (action === "remove") {
      // Remove the teacher from the current list
      setSelectedTeachers(
        selectedTeachers.filter(
          (user: ContactListType) => user._id !== contact._id
        )
      );

      // Force pop up message to dissapear
      setShowSuccessPopUp(false);
    }
  };

  /**
   * Function used when creating a new teacher inside the
   * add teacher panel
   * @param data 
   * @param processedData 
   * @returns 
   */
  const handleCreateTeacher = async (
    data: CreateEditContactType,
    processedData: CreateEditContactType
  ) => {
    // Request to create the new teacher
    const response = await createRecordRequest(
      processedData,
      "/api/users/class-admins"
    );

    // Check if the response is successful
    if (!response.successful) {
      // alert(response.message);
      return;
    }

    // Come back to the main view
    setCreateNewTeacher(false);
    // // And show a successful message
    // setShowSuccessPopUp(true);
    getTeacherData();
  };

  return (
    <>
      <motion.div
        key="add-teacher-blurred-background"
        className="fixed inset-0 w-full h-full bg-neutral-500 bg-opacity-40 backdrop-blur-[2px] z-20 flex flex-col flex-grow"
        variants={defaultBgVariants}
        initial="initial"
        animate="animate"
        exit="exit"
        transition={{ duration: 0.1, ease: "easeOut" }}
        onClick={handleClosePopUp}
      />
      {!createNewTeacher && (
        <motion.div
          key="add-teacher-main"
          variants={defaultPanelVariants}
          initial="initial"
          animate="animate"
          exit="exit"
          transition={{ duration: 0.1, ease: "easeOut" }}
          className="fixed w-[462px] h-fit max-h-[90%] left-0 right-0 ml-auto mr-auto px-6 pt-6 pb-8 bottom-5 bg-white rounded-3xl flex-col justify-start items-start gap-6 inline-flex z-30"
        >
          <div className="relative w-full h-fit max-h-[80vh] flex-col justify-start items-start gap-6 inline-flex">
            <PopUpHeader
              key="add-teacher-header"
              title="Add Staff"
              handlePressBack={handlePressBack}
              createNewOptions={{
                text: "Create new",
                onClick: () => setCreateNewTeacher(true),
              }}
            />
            <UserCardsList
              key="add-teacher-list"
              userList={teacherData}
              selectedUsers={selectedTeachers}
              isLoadingData={isLoadingData}
              primaryTags={false}
              justDisplay={false}
              selectAction={selectTeacherAction}
            />
            <SuccessPopUpMessage
              key="success-staff-message"
              name={
                lastSelectedContact.firstName +
                " " +
                lastSelectedContact.lastName
              }
              nameType="Staff"
              showSuccessPopUp={showSuccessPopUp}
              setShowSuccessPopUp={setShowSuccessPopUp}
              dismissAction={() => {}}
            />
          </div>
        </motion.div>
      )}
      {/* Create new teacher pop up */}
      {createNewTeacher && (
        <CreateEditUser
          key="create-new-teacher"
          confirmButtonText="Add Staff"
          showPrimaryOption={false}
          handlePressBack={handlePressBack}
          handleSaveFunction={handleCreateTeacher}
        />
      )}
    </>
  );
};

export default AddTeacherPanel;
